import React, { useEffect, useState } from "react";
import { CheckPicker, SelectPicker } from "rsuite";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/redux/hooks/hooks";
import useResponsiveWidth from "../../../../react-query/hooks/useResponsiveWidth";
import { updateAllocationComplianceDetails } from "../../../../services/redux/reducer/allocationCompliance.reducer";
import { useAllocationComplianceFilters } from "../../../../react-query/hooks/HCompliance";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../../react-query/queryKeys";
import { sortCommodityFirst, getDefaultCommodity } from "../../../../utils/helpers/commodityOrder";

const AllocationFilters = () => {
  const AllocationComplianceData = useAppSelector(
    (state) => state.allocationCompliance
  );
  const dispatch = useAppDispatch();
  const width = useResponsiveWidth();

  const [selectedDistrict, setSelectedDistrict] = useState<string[]>(
    AllocationComplianceData.filter.district
  );
  const [selectedCommodity, setSelectedCommodity] = useState<string>("");

  const { allocationComplianceFilters, filtersLoaded } =
    useAllocationComplianceFilters(
      localStorage.getItem("userEmail"),
      selectedCommodity
    );

  useEffect(() => {
    if (!allocationComplianceFilters) {
      return;
    }
    if (!filtersLoaded && AllocationComplianceData.filter.commodity === "") {
      dispatch(
        updateAllocationComplianceDetails({
          ...AllocationComplianceData,
          filter: {
            commodity: AllocationComplianceData.isPartnerView
              ? allocationComplianceFilters?.commodity?.[0]?.commodity
              : getDefaultCommodity(allocationComplianceFilters?.commodity),
            district: AllocationComplianceData.isPartnerView
              ? AllocationComplianceData.filter.district
                ? AllocationComplianceData.filter.district.length === 1
                  ? AllocationComplianceData.filter.district
                  : allocationComplianceFilters?.district?.[0]?.district
                : allocationComplianceFilters?.district?.[0]?.district
              : [],
            baseline: allocationComplianceFilters?.baseline?.[0]?.baseline,
            trendView: allocationComplianceFilters?.trendView?.[0]?.trend_view,
            baseSku: "",
          },
          filtersLoaded: filtersLoaded,
          isPartnerView: AllocationComplianceData.isPartnerView,
        })
      );
      setSelectedCommodity(
        AllocationComplianceData.isPartnerView
          ? allocationComplianceFilters?.commodity?.[0]?.commodity
          : getDefaultCommodity(allocationComplianceFilters?.commodity),
      );
    }
    if (
      !filtersLoaded &&
      AllocationComplianceData.filter.commodity !== "" &&
      AllocationComplianceData.filter.baseSku === ""
    ) {
      dispatch(
        updateAllocationComplianceDetails({
          ...AllocationComplianceData,
          filter: {
            commodity: AllocationComplianceData.filter.commodity,
            district: AllocationComplianceData.filter.district,
            baseline: AllocationComplianceData.filter.baseline,
            trendView: AllocationComplianceData.filter.trendView,
            baseSku: allocationComplianceFilters?.baseSKU?.[0]?.base_sku,
          },
          filtersLoaded: filtersLoaded,
          isPartnerView: AllocationComplianceData.isPartnerView,
        })
      );
      setSelectedCommodity(AllocationComplianceData.filter.commodity);
    }
  }, [allocationComplianceFilters]);

  useEffect(() => {
    dispatch(
      updateAllocationComplianceDetails({
        ...AllocationComplianceData,
        filter: {
          commodity: AllocationComplianceData.filter.commodity,
          district: AllocationComplianceData.filter.district,
          baseline: AllocationComplianceData.filter.baseline,
          trendView: AllocationComplianceData.filter.trendView,
          baseSku:
            allocationComplianceFilters?.baseSKU &&
            allocationComplianceFilters?.baseSKU.length
              ? allocationComplianceFilters?.baseSKU[0]?.base_sku
              : "",
        },
      })
    );
  }, [selectedCommodity]);

  useEffect(() => {
    dispatch(
      updateAllocationComplianceDetails({
        ...AllocationComplianceData,
        filter: {
          commodity: AllocationComplianceData.isPartnerView
            ? allocationComplianceFilters?.commodity
              ? allocationComplianceFilters?.commodity[0].commodity
              : ""
            : AllocationComplianceData.filter.commodity,
          district: AllocationComplianceData.isPartnerView
            ? AllocationComplianceData.filter.district.length
              ? [AllocationComplianceData.filter.district[0]]
              : [allocationComplianceFilters?.district[0].district]
            : AllocationComplianceData.filter.district,
          baseline: AllocationComplianceData.filter.baseline,
          trendView: AllocationComplianceData.filter.trendView,
          baseSku: AllocationComplianceData.filter.baseSku,
        },
      })
    );
    setSelectedDistrict(
      AllocationComplianceData.isPartnerView
        ? AllocationComplianceData.filter.district.length
          ? [AllocationComplianceData.filter.district[0]]
          : [allocationComplianceFilters?.district[0].district]
        : AllocationComplianceData.filter.district
    );
    setSelectedCommodity(
      AllocationComplianceData.isPartnerView
        ? allocationComplianceFilters?.commodity
          ? allocationComplianceFilters?.commodity[0].commodity
          : ""
        : AllocationComplianceData.filter.commodity
    );
  }, [AllocationComplianceData.dataLoaded]);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (selectedCommodity && localStorage.getItem("userEmail")) {
      queryClient.invalidateQueries({
        queryKey: [
          queryKeys.allocationComplianceFilters,
          localStorage.getItem("userEmail"),
          selectedCommodity,
        ],
      });
    }
  }, [selectedCommodity, queryClient]);

  return (
    <div className="flex justify-start items-center gap-8">
      <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
        <label className="font-bold text-[0.8vw]">Commodity</label>

        <SelectPicker
          menuAutoWidth
          className="dropdown-responsive-width"
          labelKey="commodity"
          valueKey="commodity"
          value={AllocationComplianceData.filter.commodity}
          placeholder="Select commodity"
          cleanable={false}
          style={{ width: width }}
          disabledItemValues={
            AllocationComplianceData.isPartnerView
              ? ["PVC Conduit", "Copper Wire"]
              : []
          }
          onChange={(value: string | null) => {
            dispatch(
              updateAllocationComplianceDetails({
                ...AllocationComplianceData,
                filter: {
                  commodity: value,
                  district: AllocationComplianceData.filter.district,
                  baseline: AllocationComplianceData.filter.baseline,
                  trendView: AllocationComplianceData.filter.trendView,
                  baseSku: "",
                },
              })
            );
            setSelectedCommodity(value || "");
          }}
          data={sortCommodityFirst(allocationComplianceFilters?.commodity)}
        ></SelectPicker>
      </div>

      <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
        <label className="font-bold text-[0.8vw]">District</label>

        {AllocationComplianceData.isPartnerView ? (
          <SelectPicker
            className="dropdown-responsive-width"
            menuAutoWidth
            placeholder="Select District"
            labelKey="district"
            style={{ width: width }}
            cleanable={false}
            value={
              AllocationComplianceData.filter.district.length !== 0
                ? AllocationComplianceData.filter.district[0]
                : ""
            }
            onChange={(value: string | null) => {
              dispatch(
                updateAllocationComplianceDetails({
                  ...AllocationComplianceData,
                  filter: {
                    commodity: AllocationComplianceData.filter.commodity,
                    district: [value],
                    baseline: AllocationComplianceData.filter.baseline,
                    trendView: AllocationComplianceData.filter.trendView,
                    baseSku: AllocationComplianceData.filter.baseSku,
                  },
                })
              );
            }}
            menuStyle={{ width: "120px" }}
            menuMaxHeight={200}
            valueKey="district"
            data={allocationComplianceFilters?.district || []}
          />
        ) : (
          <CheckPicker
            menuAutoWidth
            placeholder="All"
            className="dropdown-responsive-width"
            labelKey="district"
            cleanable={true}
            style={{ width: width }}
            onClean={() =>
              dispatch(
                updateAllocationComplianceDetails({
                  ...AllocationComplianceData,
                  filter: {
                    commodity: AllocationComplianceData.filter.commodity,
                    district: [],
                    baseline: AllocationComplianceData.filter.baseline,
                    trendView: AllocationComplianceData.filter.trendView,
                    baseSku: AllocationComplianceData.filter.baseSku,
                  },
                })
              )
            }
            value={selectedDistrict}
            valueKey="district"
            onClose={() => {
              dispatch(
                updateAllocationComplianceDetails({
                  ...AllocationComplianceData,
                  filter: {
                    commodity: AllocationComplianceData.filter.commodity,
                    district: selectedDistrict,
                    baseline: AllocationComplianceData.filter.baseline,
                    trendView: AllocationComplianceData.filter.trendView,
                    baseSku: AllocationComplianceData.filter.baseSku,
                  },
                })
              );
            }}
            menuStyle={{ width: "120px" }}
            menuMaxHeight={200}
            onChange={(value: string[]) => {
              setSelectedDistrict(value);
            }}
            data={allocationComplianceFilters?.district || []}
          />
        )}
      </div>

      <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
        <label className="font-bold text-[0.8vw]">Time Period</label>

        <SelectPicker
          menuAutoWidth
          placeholder="Select Basleline"
          labelKey="baseline"
          className="dropdown-responsive-width"
          cleanable={false}
          style={{ width: width }}
          value={AllocationComplianceData.filter.baseline}
          onChange={(value: string | null) =>
            dispatch(
              updateAllocationComplianceDetails({
                ...AllocationComplianceData,
                filter: {
                  commodity: AllocationComplianceData.filter.commodity,
                  district: AllocationComplianceData.filter.district,
                  baseline: value,
                  trendView: "Monthly",
                  baseSku: AllocationComplianceData.filter.baseSku,
                },
              })
            )
          }
          menuMaxHeight={150}
          valueKey="baseline"
          data={allocationComplianceFilters?.baseline || []}
        />
      </div>

      <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
        <label className="font-bold text-[0.8vw]">Trend View</label>

        <SelectPicker
          menuAutoWidth
          placeholder="Select View"
          labelKey="trend_view"
          className="dropdown-responsive-width"
          cleanable={false}
          style={{ width: width }}
          value={AllocationComplianceData.filter.trendView}
          disabledItemValues={
            AllocationComplianceData.filter.baseline !== "3 Months"
              ? ["Weekly"]
              : []
          }
          onChange={(value: string | null) =>
            dispatch(
              updateAllocationComplianceDetails({
                ...AllocationComplianceData,
                filter: {
                  commodity: AllocationComplianceData.filter.commodity,
                  district: AllocationComplianceData.filter.district,
                  baseline: AllocationComplianceData.filter.baseline,
                  trendView: value,
                  baseSku: AllocationComplianceData.filter.baseSku,
                },
              })
            )
          }
          menuMaxHeight={150}
          valueKey="trend_view"
          data={allocationComplianceFilters?.trendView || []}
        />
      </div>

      <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
        <label className="font-bold text-[0.8vw]">Base SKU</label>

        <SelectPicker
          menuAutoWidth
          placeholder="Select SKU"
          labelKey="base_sku"
          className="dropdown-responsive-width"
          cleanable={false}
          style={{ width: width }}
          value={AllocationComplianceData.filter.baseSku}
          onChange={(value: string | null) =>
            dispatch(
              updateAllocationComplianceDetails({
                ...AllocationComplianceData,
                filter: {
                  commodity: AllocationComplianceData.filter.commodity,
                  district: AllocationComplianceData.filter.district,
                  baseline: AllocationComplianceData.filter.baseline,
                  trendView: AllocationComplianceData.filter.trendView,
                  baseSku: value,
                },
              })
            )
          }
          menuMaxHeight={150}
          valueKey="base_sku"
          data={allocationComplianceFilters?.baseSKU || []}
        />
      </div>
    </div>
  );
};

export default AllocationFilters;
